import { FrondEngine, BlendMode } from "@frond/engine";
import { useState } from "react";
import { loadPalettes, savePalettes, PaletteEntry } from "../state/tools";

interface Props {
  engine: FrondEngine;
  version: number;
  open?: boolean;
  onClose?: () => void;
  onToggle?: () => void;
}

export function SidePanels({ engine, version, open = false, onClose, onToggle }: Props) {
  const [tab, setTab] = useState<"bookmarks" | "layers" | "palettes">("bookmarks");

  if (!open) {
    return (
      <button
        className="sidepanel-toggle"
        onClick={onToggle ?? onClose}
        title="Open panels (Bookmarks, Layers, Palettes)"
        aria-label="Open panels"
      >
        <span>🥞</span>
      </button>
    );
  }

  return (
    <>
      <div className="sidepanel-backdrop" onClick={onClose} />
      <aside className="sidepanel">
        <div className="sidepanel-head">
          <div className="sidepanel-tabs">
            <button className={tab === "bookmarks" ? "active" : ""} onClick={() => setTab("bookmarks")}>
              🔖 Bookmarks
            </button>
            <button className={tab === "layers" ? "active" : ""} onClick={() => setTab("layers")}>
              🥞 Layers
            </button>
            <button className={tab === "palettes" ? "active" : ""} onClick={() => setTab("palettes")}>
              🎨 Palettes
            </button>
          </div>
          {onClose && (
            <button className="sidepanel-close-btn" onClick={onClose} title="Close panel" aria-label="Close panel">
              ✕
            </button>
          )}
        </div>
        <div className="sidepanel-body">
          {tab === "bookmarks" && <Bookmarks engine={engine} version={version} />}
          {tab === "layers" && <Layers engine={engine} version={version} />}
          {tab === "palettes" && <Palettes engine={engine} />}
        </div>
      </aside>
    </>
  );
}

function Bookmarks({ engine }: { engine: FrondEngine; version: number }) {
  const bookmarks = engine.world.bookmarks;
  return (
    <div className="panel-list">
      {bookmarks.length === 0 && (
        <p className="panel-hint">
          Use the 🔖 tool to drop bookmarks on the canvas and jump between them instantly — even
          across zoom levels.
        </p>
      )}
      {bookmarks.map((b, i) => (
        <div className="panel-row" key={b.id}>
          <button
            className="panel-row-main"
            onClick={() => engine.jumpToBookmark(b)}
          >
            <span className="bm-icon">🔖</span>
            <span className="bm-label">{b.label || `Bookmark ${i + 1}`}</span>
            <span className="bm-depth" title="Zoom depth">
              {b.cellId.length ? `2^${b.cellId.length}` : "top"}
            </span>
          </button>
          <button className="icon-btn" onClick={() => engine.world.removeBookmark(b.id)} title="Remove">
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function Layers({ engine }: { engine: FrondEngine; version: number }) {
  const layers = engine.world.layers;
  return (
    <div className="panel-list">
      {layers.map((l, i) => (
        <div className="panel-row" key={l.id}>
          <button
            className="icon-btn"
            title={l.visible ? "Hide layer" : "Show layer"}
            onClick={() => engine.world.updateLayer(l.id, { visible: !l.visible })}
          >
            {l.visible ? "👁️" : "🚫"}
          </button>
          <input
            className="layer-name"
            value={l.name}
            onChange={(e) => engine.world.updateLayer(l.id, { name: e.target.value })}
          />
          <select
            className="layer-blend"
            value={l.blend}
            onChange={(e) => engine.world.updateLayer(l.id, { blend: e.target.value as BlendMode })}
            title="Blend mode"
          >
            <option value="normal">Normal</option>
            <option value="multiply">Multiply</option>
            <option value="screen">Screen</option>
          </select>
          <input
            className="layer-opacity"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={l.opacity}
            onChange={(e) => engine.world.updateLayer(l.id, { opacity: Number(e.target.value) })}
            title="Opacity"
          />
          {layers.length > 1 && (
            <button className="icon-btn" onClick={() => engine.world.removeLayer(l.id)} title="Delete layer">
              ✕
            </button>
          )}
        </div>
      ))}
      <button className="btn block" onClick={() => engine.world.addLayer()}>
        ＋ Add layer
      </button>
      <p className="panel-hint">
        New strokes go on the top layer. Blend modes and opacity apply per layer; erasing only
        affects the active layer.
      </p>
    </div>
  );
}

function Palettes({ engine }: { engine: FrondEngine }) {
  const [palettes, setPalettes] = useState<PaletteEntry[]>(loadPalettes());
  const apply = (colors: string[]) => {
    if (colors[0]) engine.tools.setColor(colors[0]);
  };
  const save = (next: PaletteEntry[]) => {
    setPalettes(next);
    savePalettes(next);
  };
  return (
    <div className="panel-list">
      {palettes.map((p) => (
        <div className="palette-block" key={p.name}>
          <div className="palette-head">
            <span>{p.name}</span>
            <button
              className="icon-btn"
              title="Save current color into this palette"
              onClick={() =>
                save(
                  palettes.map((q) =>
                    q.name === p.name
                      ? { ...q, colors: [engine.tools.style.color, ...q.colors].slice(0, 10) }
                      : q,
                  ),
                )
              }
            >
              ＋
            </button>
          </div>
          <div className="color-row">
            {p.colors.map((c) => (
              <button
                key={c}
                className="swatch"
                style={{ background: c }}
                onClick={() => apply(p.colors)}
                title={c}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
